import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import hairShopSrc from "$/hairShop.svg";
import hotelSrc from "$/hotel.svg";
import schoolSrc from "$/school.svg";
import dogSrc from "$/dogT.svg";
import logoSrc from "$/logo.svg";
import { ValidateCheckbox } from "@/Checkbox";
import logoOrangeSrc from "$/logoOrange.svg";
import {
  useUpdateTempReserve,
  useGetContract,
  useCreateContract,
} from "../../api/services/reservation";
import {
  useBusinessManualReserveGetContract,
  useCreateContractBusinessManual,
} from "../../api/services/business/reserve";
import {
  useContractInboxDetail,
  useSignContract,
  useUploadAttachment,
} from "~/services/customer/contractRequest";
import type { CdAttachType } from "~/types/common";
import { useForm } from "react-hook-form";
import InlineContractRenderer, {
  type InlineContractRendererRef,
} from "./InlineContractRenderer";
import RichContractRenderer from "./RichContractRenderer";
import type { ContractProgress } from "./RichContractRenderer";
import RenderedContractRenderer, {
  type RenderedContractRendererRef,
} from "./RenderedContractRenderer";
import ContractText from "./ContractText";
import AddressSearch from "./AddressSearch";
import SignatureCanvas from "./SignatureCanvas";

interface ContractModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedPet?: {
    petName: string;
    fileUrl: string | null;
    petSeq?: number;
  };
  reservationSeq?: number;
  companySeq?: number;
  userSeq?: number;
  contractSeq?: number;
  /** 'business'이면 GET/POST 모두 /business/manual/reserve/contract 사용 (GET: petSeq만, POST: 파라미터/리턴 user와 동일), 미지정 시 user API */
  contractApiMode?: "user" | "business";
  /**
   * 지정 시 'requestSeq 모드' — 즉석 폼 생성 대신 이 HIST_CONTRACT_REQUEST 를 서명한다.
   * (예약 게이트: ensure 로 확보한 대기/자동발송 계약을 통일 서명으로 처리)
   */
  requestSeq?: number;
}

type ContractStep = "introduction" | "input" | "completion";

interface ContractFormData {
  name: string;
  phone: string;
  address: string;
  detailAddress: string;
  signature: string;
  agreeToPrivacy: boolean;
}

const ContractModal = ({
  visible,
  onClose,
  onConfirm,
  selectedPet,
  reservationSeq,
  companySeq,
  userSeq,
  contractApiMode = "user",
  requestSeq,
}: ContractModalProps) => {
  // "앞으로는 설명 없이 바로 계약서를 확인할께요" — 체크 시 localStorage 에 저장하고
  // 다음 진입부터 introduction 을 건너뛰어 바로 input(계약 내용) 으로 시작한다.
  const INTRO_SKIP_KEY = "contract-intro-skip";
  const introSkip = (() => {
    try {
      return localStorage.getItem(INTRO_SKIP_KEY) === "1";
    } catch {
      return false;
    }
  })();
  const [currentStep, setCurrentStep] = useState<ContractStep>(
    introSkip ? "input" : "introduction",
  );
  const [skipExplanation, setSkipExplanation] = useState(introSkip);
  const [legacyAddress, setLegacyAddress] = useState("");
  const [legacyDetailAddress, setLegacyDetailAddress] = useState("");
  const [legacyAgreePrivacy, setLegacyAgreePrivacy] = useState(false);
  const [legacySignature, setLegacySignature] = useState("");
  // const [showImageModal, setShowImageModal] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const inlineRendererRef = useRef<InlineContractRendererRef>(null);
  const renderedRendererRef = useRef<RenderedContractRendererRef>(null);
  const [renderedProgress, setRenderedProgress] =
    useState<ContractProgress | null>(null);

  const updateTempMutation = useUpdateTempReserve();
  const userCreateContractMutation = useCreateContract();
  const businessCreateContractMutation = useCreateContractBusinessManual();
  const createContractMutation =
    contractApiMode === "business"
      ? businessCreateContractMutation
      : userCreateContractMutation;

  const { reset } = useForm<ContractFormData>({
    defaultValues: {},
    mode: "onChange",
  });

  const petSeq = selectedPet?.petSeq ?? 0;
  // requestSeq 모드: 즉석 폼 GET 대신 확보된 HIST_CONTRACT_REQUEST 상세를 계약 소스로 사용하고 /sign 으로 서명.
  const useRequestMode = !!requestSeq;
  const useUserContract = !useRequestMode && contractApiMode !== "business";
  const useBusinessContract = !useRequestMode && contractApiMode === "business";

  const signContractMutation = useSignContract();
  const uploadAttachMutation = useUploadAttachment();
  const { data: requestDetail } = useContractInboxDetail(
    visible && useRequestMode ? requestSeq : undefined,
  );

  // 첨부파일 (예방접종증명서 등) — requestSeq 모드에서만: 업로드는 requestSeq 에 즉시 링크됨(서명 페이로드와 별개)
  type AttachKind = "VACCINATION" | "HEALTH_CHECK" | "ID_CARD" | "OTHER";
  interface AttachItem {
    id: string;
    kind: AttachKind;
    name: string;
    size: number;
    file: File;
  }
  const ATTACH_LABEL: Record<AttachKind, string> = {
    VACCINATION: "예방접종 증명서",
    HEALTH_CHECK: "건강검진 결과",
    ID_CARD: "신분증",
    OTHER: "기타",
  };
  const [attachments, setAttachments] = useState<AttachItem[]>([]);
  const [attachKind, setAttachKind] = useState<AttachKind>("VACCINATION");
  const [attachOpen, setAttachOpen] = useState(true);

  const { data: userContractData } = useGetContract(
    companySeq || 0,
    petSeq,
    useUserContract && !!companySeq && !!petSeq,
  );
  const { data: businessContractData } = useBusinessManualReserveGetContract(
    petSeq,
    useBusinessContract && !!petSeq,
  );

  const contractData = useBusinessContract
    ? businessContractData
    : userContractData;
  const contract = useRequestMode ? (requestDetail as any) : contractData?.data;
  const hasAuthoredContract = !!(
    contract?.contractDocJson || contract?.contractContents
  );
  const useLegacyDefaultContract = !!contract && !hasAuthoredContract;
  const hasRenderedContract = !!(
    contract?.renderedPageFileUrls?.length && contract?.placementManifest
  );
  const legacyContractDate =
    contract?.variables?.contractDate ??
    new Date()
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\./g, "-")
      .replace(/\s/g, "")
      .replace(/-$/, "");

  // 모달이 열릴 때 '설명 건너뛰기'가 저장돼 있으면 introduction 을 건너뛰고 바로 계약 내용으로.
  useEffect(() => {
    if (visible && skipExplanation) setCurrentStep("input");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible || !contract) return;
    setLegacyAddress(contract.userInfo?.address ?? "");
    setLegacyDetailAddress("");
    setLegacyAgreePrivacy(false);
    setLegacySignature("");
  }, [
    visible,
    contract?.companySeq,
    contract?.petSeq,
    contract?.userInfo?.address,
  ]);
  // API 데이터를 폼에 설정
  // useEffect(() => {
  //   if (contractData?.data) {
  //     setValue("name", contractData.data.userName || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("phone", contractData.data.phoneNumber || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("address", contractData.data.addr || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("detailAddress", contractData.data.addrDetail || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("agreeToPrivacy", contractData.data.privacyYn === "Y", {
  //       shouldValidate: true,
  //     });
  //   }
  // }, [contractData, setValue]);

  // 계약 생성 성공 시 다음 단계로 이동
  useEffect(() => {
    if (createContractMutation.isSuccess) {
      setCurrentStep("completion");
      // 모달이 열린 후 스크롤을 최상단으로 이동
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    }
  }, [createContractMutation.isSuccess]);

  const handleNext = () => {
    if (currentStep === "introduction") {
      setCurrentStep("input");
      // 모달이 열린 후 스크롤을 최상단으로 이동
      // setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
      // }, 50);
    } else if (currentStep === "input") {
      setCurrentStep("completion");
    }
  };

  const handleBack = () => {
    if (currentStep === "input") {
      setCurrentStep("introduction");
      // setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
      // }, 50);
    } else if (currentStep === "completion") {
      setCurrentStep("input");
    }
  };

  const handleConfirm = () => {
    setCurrentStep("introduction");
    onConfirm();
  };

  const handleClose = () => {
    // 완료 단계가 아닐 때만 취소 API 호출
    if (reservationSeq && currentStep !== "completion") {
      updateTempMutation.mutate({
        reservationSeq,
        contractCancel: true,
      });
    }

    setCurrentStep("introduction");
    setLegacyAddress("");
    setLegacyDetailAddress("");
    setLegacyAgreePrivacy(false);
    setLegacySignature("");
    setAttachments([]);
    reset();
    onClose();
  };

  const submitContract = async (
    contractRequestData: any,
    signature: string,
  ) => {
    const mutationOptions = {
      onSuccess: () => {
        setCurrentStep("completion");
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      },
      onError: (error: Error) => {
        console.error("계약 처리 실패:", error);
        alert("계약 처리에 실패했습니다. 다시 시도해주세요.");
      },
    };

    // requestSeq 모드: 확보된 계약 요청을 통일 서명(/sign)으로 처리 (신규 폼 생성 안 함)
    if (useRequestMode && requestSeq) {
      // 서명 확정 직전에만 첨부 업로드 → 서명하지 않고 닫으면 첨부가 남지 않는다(orphan 방지).
      if (attachments.length > 0) {
        try {
          for (const a of attachments) {
            await uploadAttachMutation.mutateAsync({
              requestSeq,
              file: a.file,
              attachType: ("ATTACH_TYPE_" + a.kind) as CdAttachType,
            });
          }
        } catch (err) {
          console.error(err);
          alert("첨부파일 업로드에 실패했어요. 다시 시도해 주세요.");
          return;
        }
      }
      signContractMutation.mutate(
        {
          requestSeq,
          signatureDataUrl: signature,
          fieldValues: contractRequestData.fieldValues ?? {},
        },
        mutationOptions,
      );
      return;
    }

    const mutationPayload = {
      data: contractRequestData,
      signature,
    };
    if (contractApiMode === "business") {
      businessCreateContractMutation.mutate(mutationPayload, mutationOptions);
    } else {
      userCreateContractMutation.mutate(mutationPayload, mutationOptions);
    }
  };

  const onSubmit = (_data: ContractFormData) => {
    if (!contract) {
      alert("계약서 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    if (useLegacyDefaultContract) {
      if (!legacyAddress.trim()) {
        alert("주소를 검색 후 선택해주세요.");
        return;
      }
      if (!legacyAgreePrivacy) {
        alert("개인정보 수집에 동의해주세요.");
        return;
      }
      if (!legacySignature) {
        alert("서명을 해주세요.");
        return;
      }

      submitContract(
        {
          ...contract,
          contractName: contract.contractName || "반려동물 위탁보호 신청계약서",
          contractContents: contract.contractContents || "",
          contractDocJson: contract.contractDocJson ?? null,
          privacyYn: "Y",
          addr: legacyAddress,
          addrDetail: legacyDetailAddress,
          ...(contractApiMode === "business" && userSeq != null && { userSeq }),
        },
        legacySignature,
      );
      return;
    }

    // 렌더 계약서는 RenderedContractRenderer, 구형(docJson/텍스트)은 Inline/Rich 렌더러에서 값 수집
    const activeRenderer = hasRenderedContract
      ? renderedRendererRef.current
      : inlineRendererRef.current;
    const inlineValues = activeRenderer?.getValues();
    const validationError = activeRenderer?.validate();
    if (validationError) {
      alert(validationError);
      return;
    }

    // [[필드:address|guardianAddress|...]] 토큰 → BE addr / addrDetail 매핑
    // 인라인 렌더러가 주소 검색(다음 우편번호) 결과를 fields[key] 에, 상세주소를 fields[key+"Detail"] 에 저장
    const addr =
      inlineValues?.fields["guardianAddress"] ??
      inlineValues?.fields["address"] ??
      "";
    const addrDetail =
      inlineValues?.fields["guardianAddressDetail"] ??
      inlineValues?.fields["guardianAddrDetail"] ??
      inlineValues?.fields["addressDetail"] ??
      inlineValues?.fields["addrDetail"] ??
      "";

    // 체크박스 중 하나라도 체크되면 privacyYn = Y (양식에 체크박스가 여러 개 있을 수 있음)
    const anyChecked = inlineValues
      ? Object.values(inlineValues.checkboxes).some((v) => v)
      : false;
    const privacyYn = anyChecked ? "Y" : "N";
    const signature = inlineValues?.signature ?? null;
    const fieldValues: Record<string, string> = {};
    Object.entries(inlineValues?.fields ?? {}).forEach(([key, value]) => {
      if (value != null && value !== "") {
        fieldValues[key] = String(value);
      }
    });
    Object.entries(inlineValues?.checkboxes ?? {}).forEach(([key, value]) => {
      fieldValues[key] = value ? "Y" : "N";
    });

    if (!signature) {
      alert("서명을 해주세요.");
      return;
    }

    const contractRequestData = {
      ...contract,
      privacyYn,
      addr,
      addrDetail,
      fieldValues,
      ...(contractApiMode === "business" && userSeq != null && { userSeq }),
    } as any;

    submitContract(contractRequestData, signature);
  };

  // const handleTrimOnBlur = (fieldName: keyof SignUpBusinessDetailState) => {
  //   const currentValue = watch(fieldName);
  //   if (typeof currentValue === "string" && currentValue.endsWith(" ")) {
  //     setValue(fieldName, currentValue.trim(), { shouldValidate: true });
  //   }
  // };

  const renderedRemaining =
    hasRenderedContract && renderedProgress
      ? Math.max(0, renderedProgress.total - renderedProgress.done)
      : 0;
  const needsMoreInput = hasRenderedContract && renderedRemaining > 0;
  const isSubmitting =
    createContractMutation.isPending || signContractMutation.isPending;

  // 첨부파일 섹션 — requestSeq 모드에서만(업로드는 requestSeq 에 링크). 독립 서명화면(contract-sign)과 동일 UI.
  const attachmentSection =
    useRequestMode && requestSeq ? (
      <div className="mt-3 max-h-[40%] shrink-0 overflow-y-auto rounded-2xl border border-[#EEE] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <button
          type="button"
          onClick={() => setAttachOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3.5 text-left"
        >
          <span className="flex items-center gap-2 text-[13px] font-bold text-[#333]">
            📎 첨부파일{" "}
            <span className="text-[11px] font-medium text-[#AAA]">선택</span>
            {attachments.length > 0 && (
              <span className="rounded-full bg-[#FFF3E5] px-2 py-0.5 text-[11px] font-bold text-[#E08600]">
                {attachments.length}
              </span>
            )}
          </span>
          <span
            className={`text-[#BBB] transition-transform ${attachOpen ? "rotate-180" : ""}`}
          >
            ⌄
          </span>
        </button>
        {attachOpen && (
          <div className="px-4 pb-4">
            <div className="mb-2 text-[11px] text-[#666]">
              예방접종 증명서 등 사업장에 함께 전달할 자료가 있으면 첨부해
              주세요
            </div>
            <div className="mb-2 grid grid-cols-4 gap-1.5">
              {(Object.keys(ATTACH_LABEL) as AttachKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setAttachKind(k)}
                  className={`rounded-full border py-1.5 text-[10px] ${
                    attachKind === k
                      ? "border-[#FF9934] bg-[#FFF7E6] font-semibold text-[#FF9934]"
                      : "border-[#E9E9E9] bg-white text-[#666]"
                  }`}
                >
                  {ATTACH_LABEL[k]}
                </button>
              ))}
            </div>
            <label className="block w-full cursor-pointer rounded-lg border-2 border-dashed border-[#E9E9E9] py-3 text-center text-[12px] text-[#666] hover:border-[#FF9934]">
              <input
                type="file"
                accept="image/*,.pdf"
                hidden
                onClick={() => {
                  (window as any).__mungFileChooserKind = "contract-attachment";
                }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f || !requestSeq) return;
                  if (f.size > 10 * 1024 * 1024) {
                    alert("10MB 이하 파일만 첨부할 수 있어요");
                    return;
                  }
                  // 즉시 업로드하지 않고 클라이언트에만 담아둔다 → 실제 업로드는 서명 제출 시점.
                  const item: AttachItem = {
                    id: `${Date.now()}-${Math.random()}`,
                    kind: attachKind,
                    name: f.name,
                    size: f.size,
                    file: f,
                  };
                  setAttachments((prev) => [...prev, item]);
                  e.target.value = "";
                }}
              />
              📎 {ATTACH_LABEL[attachKind]} 파일 선택
            </label>
            {attachments.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 rounded bg-[#FAFAFA] p-2"
                  >
                    <span className="rounded bg-[#FFF7E6] px-1.5 py-0.5 text-[10px] font-bold text-[#FF9934]">
                      {ATTACH_LABEL[a.kind]}
                    </span>
                    <span className="flex-1 truncate text-[11px]">
                      {a.name}
                    </span>
                    <span className="text-[10px] text-[#999]">
                      {(a.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setAttachments(attachments.filter((x) => x.id !== a.id))
                      }
                      className="px-1 text-[12px] text-[#F24747]"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    ) : null;

  // 풀스크린 서명 오버레이가 열려 있는 동안 배경 스크롤 잠금
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex justify-center bg-black/40 md:py-6">
      <div className="flex h-full w-full max-w-[480px] flex-col overflow-hidden bg-[#F7F7F8] md:rounded-3xl md:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.35)]">
        {/* Header */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#EEE] bg-white px-4">
          <div className="w-6" />
          <h2 className="text-[16px] font-bold text-[#111]">
            반려동물 위탁 계약
          </h2>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded text-[20px] leading-none text-[#666] hover:bg-[#F5F5F5]"
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          ref={modalContentRef}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          {currentStep === "introduction" && (
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* Introduction */}
              <div className="mb-6 text-center">
                <div className="flex justify-center items-center mx-auto mb-3 w-16 h-16 bg-orange-400 rounded-full">
                  <img
                    src={logoOrangeSrc}
                    alt="로고"
                    className="w-20 h-20 bg-gray-50 rounded-full"
                  />
                </div>
                <p className="text-sm text-center text-gray-600">
                  반려동물 위탁관리 서비스를 위하여
                  <br />
                  보호자님은 등록을 희망하시는 위탁관리 사업장과
                  <br />
                  반려견 위탁보호 계약이 필요해요.
                </p>
              </div>

              {/* Service Categories */}
              <div className="rounded-lg border border-gray-300">
                <div className="p-4 mb-1 bg-white rounded-lg">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-2 w-12 h-12 bg-gray-100 rounded-full">
                        <img
                          src={schoolSrc}
                          alt="유치원 이미지"
                          className="w-20 h-20 bg-gray-50 rounded-full"
                        />
                      </div>
                      <span className="text-xs text-[#666666]">유치원</span>
                    </div>
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-2 w-12 h-12 bg-gray-100 rounded-full">
                        <img
                          src={hairShopSrc}
                          alt="미용실"
                          className="w-20 h-20 bg-gray-50 rounded-full"
                        />
                      </div>
                      <span className="text-xs text-[#666666]">미용실</span>
                    </div>
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-2 w-12 h-12 bg-gray-100 rounded-full">
                        <img
                          src={dogSrc}
                          alt="미용실"
                          className="w-20 h-20 bg-gray-50 rounded-full"
                        />
                      </div>
                      <span className="text-xs text-[#666666]">합숙훈련소</span>
                    </div>
                    <div className="text-center">
                      <div className="flex justify-center items-center mx-auto mb-2 w-12 h-12 bg-gray-100 rounded-full">
                        <img
                          src={hotelSrc}
                          alt="호텔 이미지"
                          className="w-20 h-20 bg-gray-50 rounded-full"
                        />
                      </div>
                      <span className="text-xs text-[#666666]">호텔링</span>
                    </div>
                  </div>
                </div>
                <div className="border-b border-dotted"></div>
                {/* Optionality Note */}
                <p className="px-4 my-4 text-sm text-center text-[#666666]">
                  계약 진행은 의무사항이 아니예요.
                  <br />
                  단, 계약 미 진행 시 반려견의 유치원, 미용 등 위탁시설의 이용을
                  할 수 없어요.
                </p>
              </div>

              {/* Contract Terms */}
              <div className="my-4">
                <h3 className="mb-3 text-sm font-semibold">
                  계약을 위하여 아래의 내용을 확인해주세요
                </h3>
                <div className="text-sm text-gray-600">
                  <div className="flex">
                    <span className="mr-2">1.</span>
                    <span>계약에서 시설의 위치 및 설비 정보를 제공해요</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">2.</span>
                    <span>
                      어떻게 반려견을 위탁보호하는지를 서면으로 제공해요
                    </span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">3.</span>
                    <span>불의의 사고 등에 대한 대응정보를 제공해요</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">4.</span>
                    <span>보호자와 시설책임자의 의무사항을 설명해요</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">5.</span>
                    <span>
                      본 계약은 해당 시설과의 계약을 의미하고, 다른 서비스를
                      이용할 경우에는 새로운 위탁 보호 계약을 진행해요
                    </span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">6.</span>
                    <span>
                      계약은 1년간 유효하며, 계약 만료 후에는 재 계약이
                      진행되요.
                    </span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">7.</span>
                    <span>
                      한번 계약이 이루어진 시설은 계약 기간이 종료되기 전까지
                      별도의 재 계약이 필요 없어요.
                    </span>
                  </div>
                  <div className="flex">
                    <span className="mr-2">8.</span>
                    <span>
                      보호자의 날인이 이루어지면 계약은 즉시 효력을 발휘해요.
                    </span>
                  </div>
                </div>
              </div>
              {/* Checkbox */}
              <div className="flex items-center mb-2">
                <ValidateCheckbox
                  defaultChecked={skipExplanation}
                  onChange={() =>
                    setSkipExplanation((v) => {
                      const nv = !v;
                      try {
                        localStorage.setItem(INTRO_SKIP_KEY, nv ? "1" : "0");
                      } catch {
                        /* ignore */
                      }
                      return nv;
                    })
                  }
                />

                <label
                  htmlFor="skipExplanation"
                  className="text-sm text-gray-600"
                >
                  앞으로는 설명 없이 바로 계약서를 확인할께요
                </label>
              </div>
            </div>
          )}

          {currentStep === "input" && (
            <div className="flex min-h-0 flex-1 flex-col px-4 pt-3 pb-2">
              {attachmentSection}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#EEE] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <div className="flex shrink-0 items-center gap-2 border-b border-[#F0F0F0] px-4 py-2.5">
                  <span className="h-3.5 w-1 rounded-full bg-[#FF9934]" />
                  <div className="text-[13px] font-bold text-[#333]">
                    계약 내용
                  </div>
                </div>
                {useLegacyDefaultContract ? (
                  <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
                    <ContractText
                      guardianName={
                        contract.userName || contract.userInfo?.userName || ""
                      }
                      guardianNickname={contract.userInfo?.nickname || ""}
                      guardianEmail={contract.userInfo?.email || ""}
                      guardianPhone={
                        contract.phoneNumber ||
                        contract.userInfo?.phoneNumber ||
                        ""
                      }
                      guardianAddress={`${legacyAddress || ""} ${legacyDetailAddress || ""}`.trim()}
                      petName={
                        contract.petInfo?.petName || contract.petName || ""
                      }
                      petBirthday={contract.petInfo?.petBirthday || ""}
                      petBreed={contract.petInfo?.petBreed || ""}
                      petWeight={contract.petInfo?.petWeight || ""}
                      petNeutered={contract.petInfo?.neuter || ""}
                      petVaccinations={contract.petInfo?.inoculation || ""}
                      petPersonality={contract.petInfo?.personality || ""}
                      petAllergies={contract.petInfo?.allergy || ""}
                      petOther={contract.petInfo?.memo || ""}
                      companyName={contract.companyInfo?.companyName || ""}
                      businessNumber={
                        contract.companyInfo?.businessNumber || ""
                      }
                      contractDate={legacyContractDate}
                      signatureFileUrl={legacySignature || undefined}
                    />

                    <div className="space-y-4 rounded-lg border border-[#EEEEEE] bg-[#FAFAFA] p-4">
                      <div className="space-y-2">
                        <label className="text-sm font-bold">주소 입력</label>
                        <AddressSearch
                          defaultValue={legacyAddress}
                          onSelect={(address) => {
                            setLegacyAddress(address.address_name);
                          }}
                        />
                        <input
                          value={legacyDetailAddress}
                          onChange={(e) =>
                            setLegacyDetailAddress(e.target.value)
                          }
                          placeholder="상세주소를 입력하세요"
                          className="w-full rounded border border-[#DDDDDD] px-3 py-2 text-sm focus:outline-none focus:border-[#FFB330]"
                        />
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-sm font-bold">
                          개인정보 수집에 대한 동의
                        </h3>
                        <p className="text-xs text-gray-600">
                          입력하신 개인정보는 위탁계약에 필요한 최소한의
                          개인정보 수집을 목적으로 하며, 계약 목적이 상실되는
                          경우 즉시 폐기됩니다.
                        </p>
                        <ValidateCheckbox
                          checked={legacyAgreePrivacy}
                          onChange={(e) =>
                            setLegacyAgreePrivacy(e.target.checked)
                          }
                          title="동의"
                        />
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-sm font-bold">보호자 서명</h3>
                        <SignatureCanvas
                          onSignatureChange={setLegacySignature}
                          width={300}
                          height={150}
                        />
                      </div>
                    </div>
                  </div>
                ) : hasRenderedContract ? (
                  <RenderedContractRenderer
                    ref={renderedRendererRef}
                    pageUrls={contract.renderedPageFileUrls ?? []}
                    placementManifest={contract.placementManifest}
                    variables={contract.variables ?? {}}
                    initialAddress={contract.userInfo?.address ?? undefined}
                    sealImageUrl={contract.sealImageUrl ?? undefined}
                    onProgressChange={setRenderedProgress}
                    fillHeight
                    className="min-h-0 flex-1 px-2 pb-1"
                  />
                ) : contractData?.data?.contractDocJson ? (
                  <div className="min-h-0 flex-1 overflow-auto p-4">
                    <RichContractRenderer
                      ref={inlineRendererRef as unknown as never}
                      contractDocJson={contractData.data.contractDocJson}
                      variables={contractData.data.variables ?? {}}
                      initialAddress={
                        contractData.data.userInfo?.address ?? undefined
                      }
                      sealImageUrl={contractData.data.sealImageUrl ?? undefined}
                    />
                  </div>
                ) : contractData?.data?.contractContents ? (
                  <div className="min-h-0 flex-1 overflow-auto p-4">
                    <InlineContractRenderer
                      ref={inlineRendererRef}
                      contractContents={contractData.data.contractContents}
                      variables={contractData.data.variables ?? {}}
                      initialAddress={
                        contractData.data.userInfo?.address ?? undefined
                      }
                      sealImageUrl={contractData.data.sealImageUrl ?? undefined}
                    />
                  </div>
                ) : (
                  <div className="flex-1 py-10 text-center text-[13px] text-[#999]">
                    계약서 내용을 불러오는 중...
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === "completion" && (
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {/* Success Message */}
              <div className="mb-6 mt-4 text-center">
                <div className="flex justify-center items-center mx-auto mb-3 w-16 h-16 bg-orange-400 rounded-full">
                  <img
                    src={logoSrc}
                    alt="완료"
                    className="w-20 h-20 bg-gray-50 rounded-full"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {contractData?.data?.companyInfo?.companyName} 서비스 이용을
                  위한 {contractData?.data?.userInfo?.userName}(
                  {contractData?.data?.petInfo?.petName}) 님의
                  <br />
                  반려동물 위탁 계약이 완료되었습니다.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 하단 액션 독 — 컬럼 하단 고정, 얇은 진행바 + 스텝별 버튼 */}
        <div
          className="shrink-0 border-t border-[#EEE] bg-white/95 px-3 pt-2.5 backdrop-blur"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
          }}
        >
          {currentStep === "input" &&
            hasRenderedContract &&
            renderedProgress &&
            renderedProgress.total > 0 && (
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`shrink-0 text-[11px] font-bold ${
                    renderedRemaining === 0
                      ? "text-[#22A55B]"
                      : "text-[#E08600]"
                  }`}
                >
                  {renderedRemaining === 0
                    ? "작성 완료 ✓"
                    : `입력 ${renderedProgress.done}/${renderedProgress.total}`}
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#F0F0F0]">
                  <div
                    className={`h-full rounded-full transition-all ${
                      renderedRemaining === 0 ? "bg-[#22A55B]" : "bg-[#FF9934]"
                    }`}
                    style={{
                      width: `${Math.round((renderedProgress.done / renderedProgress.total) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

          {currentStep === "introduction" && (
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="flex-1 rounded-2xl border border-[#E9E9E9] py-3 text-[13px] font-semibold text-[#888]"
              >
                취소
              </button>
              <button
                onClick={handleNext}
                className="flex-[2] rounded-2xl bg-[#FF9934] py-3 text-[14px] font-bold text-white"
              >
                계약서 내용 확인하기
              </button>
            </div>
          )}

          {currentStep === "input" && (
            <div className="flex gap-2">
              <button
                onClick={handleBack}
                className="flex-1 rounded-2xl border border-[#E9E9E9] py-3 text-[13px] font-semibold text-[#888]"
              >
                이전
              </button>
              <button
                onClick={() => {
                  if (hasRenderedContract && needsMoreInput) {
                    // 미완료 항목을 순차로: 첫 항목으로 이동 + 입력 팝업 → 저장 시 다음 팝업 자동
                    renderedRendererRef.current?.startGuidedInput();
                    return;
                  }
                  onSubmit({} as any);
                }}
                disabled={isSubmitting}
                className={`flex-[2] rounded-2xl py-3 text-[14px] font-bold ${
                  hasRenderedContract && needsMoreInput
                    ? "bg-[#FFE7CC] text-[#B26A00]"
                    : "bg-[#FF9934] text-white disabled:bg-[#E9E9E9] disabled:text-[#999]"
                }`}
              >
                {isSubmitting
                  ? "처리중..."
                  : hasRenderedContract
                    ? needsMoreInput
                      ? `✍️ 남은 항목 ${renderedRemaining}개 입력하기`
                      : "✅ 서명하고 제출하기"
                    : "서명 완료"}
              </button>
            </div>
          )}

          {currentStep === "completion" && (
            <button
              onClick={handleConfirm}
              className="w-full rounded-2xl bg-[#FF9934] py-3 text-[14px] font-bold text-white"
            >
              확인
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ContractModal;
